import { createFileRoute } from '@tanstack/react-router';
import { Shield, Lock, Eye, Server, Users, FileCheck, AlertTriangle, RefreshCw } from "lucide-react";
import { ContentHeader, ContentSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/legal/security')({
  component: SecurityPage,
  head: () => ({
    meta: [
      { title: 'Security | Matricks' },
      { name: 'description', content: 'Learn about Matricks security practices, certifications, and how we protect your data.' },
    ],
  }),
});

const securityPillars = [
  {
    icon: Lock,
    title: "Encryption",
    description: "All data encrypted in transit and at rest using industry-standard protocols.",
  },
  {
    icon: Shield,
    title: "Access Control",
    description: "Fine-grained permissions with role-based and attribute-based access control.",
  },
  {
    icon: Eye,
    title: "Monitoring",
    description: "24/7 security monitoring with real-time threat detection and alerting.",
  },
  {
    icon: Server,
    title: "Infrastructure",
    description: "Enterprise-grade cloud infrastructure with redundancy and disaster recovery.",
  },
  {
    icon: Users,
    title: "Authentication",
    description: "Multi-factor authentication, SSO, and SAML 2.0 support for all accounts.",
  },
  {
    icon: FileCheck,
    title: "Compliance",
    description: "SOC 2 Type II certified with GDPR and HIPAA compliance capabilities.",
  },
];

function SecurityPage() {
  return (
    <div className="py-16 sm:py-24">
      <div className="container px-6">
        <ContentHeader
          title="Security"
          description="Enterprise-grade security to protect your data and integrations."
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Legal", href: "/legal" },
            { label: "Security" },
          ]}
        />

        {/* Security Pillars */}
        <ContentSection id="pillars" title="Security Pillars">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityPillars.map((pillar) => (
              <div key={pillar.title} className="rounded-xl border bg-card p-6">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <pillar.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{pillar.title}</h3>
                <p className="text-muted-foreground">{pillar.description}</p>
              </div>
            ))}
          </div>
        </ContentSection>

        {/* Data Protection */}
        <ContentSection id="data-protection" title="Data Protection" background="muted">
          <div className="space-y-6">
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg mb-4">Encryption Standards</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">In Transit</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>TLS 1.3 for all communications</li>
                    <li>Perfect Forward Secrecy (PFS)</li>
                    <li>Strong cipher suites only</li>
                    <li>Certificate pinning for mobile apps</li>
                    <li>HSTS enabled on all endpoints</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">At Rest</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>AES-256 encryption for all data</li>
                    <li>Hardware Security Modules (HSM)</li>
                    <li>Encrypted database backups</li>
                    <li>Secure key management</li>
                    <li>Regular key rotation</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg mb-4">Secrets Management</h3>
              <p className="text-muted-foreground mb-4">
                All sensitive data including API keys, passwords, and tokens are securely managed:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li>Credentials encrypted using customer-specific keys</li>
                <li>Zero plaintext storage of secrets</li>
                <li>Automatic credential rotation support</li>
                <li>Audit logging for all secret access</li>
                <li>Integration with enterprise vault solutions</li>
              </ul>
            </div>
          </div>
        </ContentSection>

        {/* Access Control */}
        <ContentSection id="access-control" title="Access Control">
          <div className="space-y-6">
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg mb-4">Authentication</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">User Authentication</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>Multi-factor authentication (MFA)</li>
                    <li>Single Sign-On (SSO)</li>
                    <li>SAML 2.0 and OIDC support</li>
                    <li>Social login integration</li>
                    <li>Passwordless authentication options</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">API Authentication</h4>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li>API key authentication</li>
                    <li>OAuth 2.0 token support</li>
                    <li>JWT token validation</li>
                    <li>IP allowlisting</li>
                    <li>Rate limiting per key</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg mb-4">Authorization</h3>
              <p className="text-muted-foreground mb-4">
                Fine-grained access control ensures users only access what they need:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg bg-muted/50 p-4">
                  <h4 className="font-medium mb-2">Role-Based Access Control (RBAC)</h4>
                  <p className="text-muted-foreground text-sm">
                    Define roles with specific permissions. Assign users to roles for consistent
                    access management across your organization.
                  </p>
                </div>
                <div className="rounded-lg bg-muted/50 p-4">
                  <h4 className="font-medium mb-2">Attribute-Based Access Control (ABAC)</h4>
                  <p className="text-muted-foreground text-sm">
                    Create dynamic policies based on user attributes, resource properties, and
                    environmental context for granular control.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </ContentSection>

        {/* Infrastructure Security */}
        <ContentSection id="infrastructure" title="Infrastructure Security" background="muted">
          <div className="space-y-6">
            <div className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg mb-4">Cloud Infrastructure</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>Hosted on enterprise-grade cloud providers (AWS, GCP, Azure)</li>
                <li>Multi-region deployment with automatic failover</li>
                <li>Virtual Private Cloud (VPC) isolation</li>
                <li>Network segmentation and firewalls</li>
                <li>DDoS protection and mitigation</li>
                <li>Regular infrastructure security assessments</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">High Availability</h3>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>99.9% uptime SLA for enterprise plans</li>
                  <li>Automatic scaling based on demand</li>
                  <li>Load balancing across multiple zones</li>
                  <li>Zero-downtime deployments</li>
                  <li>Real-time health monitoring</li>
                </ul>
              </div>
              <div className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold mb-4">Disaster Recovery</h3>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Automated daily backups</li>
                  <li>Point-in-time recovery capability</li>
                  <li>Cross-region backup replication</li>
                  <li>Documented recovery procedures</li>
                  <li>Regular disaster recovery testing</li>
                </ul>
              </div>
            </div>
          </div>
        </ContentSection>

        {/* Security Operations */}
        <ContentSection id="operations" title="Security Operations">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Eye className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Monitoring & Detection</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>24/7 Security Operations Center (SOC)</li>
                  <li>Real-time threat detection and alerting</li>
                  <li>Intrusion detection systems (IDS/IPS)</li>
                  <li>Anomaly detection using machine learning</li>
                  <li>Centralized log management and analysis</li>
                </ul>
              </div>

              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Incident Response</h3>
                </div>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Documented incident response procedures</li>
                  <li>Dedicated security incident team</li>
                  <li>Rapid response and containment</li>
                  <li>Customer notification within 72 hours</li>
                  <li>Post-incident analysis and reporting</li>
                </ul>
              </div>
            </div>

            <div className="rounded-xl border bg-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <RefreshCw className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Vulnerability Management</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Regular vulnerability scanning</li>
                  <li>Annual penetration testing by third parties</li>
                  <li>Automated dependency scanning</li>
                  <li>Secure code review practices</li>
                </ul>
                <ul className="space-y-2 text-muted-foreground text-sm">
                  <li>Bug bounty program</li>
                  <li>Rapid patch deployment process</li>
                  <li>CVSS-based prioritization</li>
                  <li>Security release notifications</li>
                </ul>
              </div>
            </div>
          </div>
        </ContentSection>

        {/* Report Vulnerability */}
        <ContentSection id="vulnerability-reporting" title="Report a Vulnerability" background="muted">
          <div className="rounded-xl border bg-card p-6">
            <p className="text-muted-foreground mb-4">
              We take security seriously and appreciate responsible disclosure of potential
              vulnerabilities. If you discover a security issue:
            </p>
            <ul className="space-y-2 text-muted-foreground mb-4">
              <li>
                Email us at{" "}
                <a href="mailto:security@matricks.ai" className="text-primary hover:underline">
                  security@matricks.ai
                </a>
              </li>
              <li>Include detailed steps to reproduce the issue</li>
              <li>Allow us reasonable time to address the issue before public disclosure</li>
              <li>Do not access or modify data that does not belong to you</li>
            </ul>
            <p className="text-muted-foreground">
              We commit to acknowledging reports within 24 hours and providing regular updates on
              our investigation and remediation progress.
            </p>
          </div>
        </ContentSection>

        {/* CTA */}
        <CTASection
          title="Questions About Security?"
          description="Contact our security team to learn more about how we protect your data."
          primaryAction={{ label: "Contact Security Team", href: "/contact" }}
          secondaryAction={{ label: "View Compliance", href: "/legal/compliance" }}
          background="primary"
        />
      </div>
    </div>
  );
}
